import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import * as XLSX from "xlsx";
import { type CellData, parseSheet } from "./excel-parse";
import { useExcelColumnVisibility } from "./useExcelColumnVisibility";

const READ_OPTS: XLSX.ParsingOptions = {
  type: "array",
  cellStyles: true,
  cellFormula: true,
  cellDates: true,
  cellNF: true,
  sheetStubs: true,
};

/**
 * Parses ONE Excel buffer (memoized) and owns the editable view state for it —
 * active sheet, column widths, row heights, and column visibility.
 *
 * The ExcelViewer instantiates this once per buffer: the received file and,
 * for STK-1269, the per-order source-rows slice. Because each buffer keeps its
 * own instance, flipping between them is a cheap pointer swap — no `XLSX.read`
 * or `parseSheet` re-run (the multi-thousand-row file is parsed only once for
 * its lifetime) and no reset of the user's resizing / hidden columns (each view
 * remembers its own). This is what made toggling laggy and jumpy before.
 */
export function useSheetView(buffer: ArrayBuffer | Uint8Array | undefined) {
  // Parse-once. Keyed on the buffer identity, so a genuinely new file re-reads
  // but a mere view toggle reuses this cached workbook.
  const workbook = useMemo(() => {
    if (!buffer) return null;
    try {
      return XLSX.read(buffer, READ_OPTS);
    } catch (error) {
      console.error("Error parsing Excel file:", error);
      return null;
    }
  }, [buffer]);

  const sheetNames = useMemo(() => workbook?.SheetNames ?? [], [workbook]);

  const [activeSheet, setActiveSheet] = useState("");
  // Keep the selected sheet valid for the current workbook (default: first).
  useEffect(() => {
    if (sheetNames.length === 0) {
      if (activeSheet !== "") setActiveSheet("");
    } else if (!sheetNames.includes(activeSheet)) {
      setActiveSheet(sheetNames[0]);
    }
  }, [sheetNames, activeSheet]);

  // Pure derivation of the rendered sheet. Recomputes only when this view's
  // workbook or selected sheet actually changes — never on a view toggle.
  const parsed = useMemo(
    () => (workbook && activeSheet ? parseSheet(workbook, activeSheet) : null),
    [workbook, activeSheet],
  );

  const [sheetData, setSheetData] = useState<CellData[][]>([]);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [rowHeights, setRowHeights] = useState<number[]>([]);
  // Absolute 1-based physical sheet row (A1 = 1) for each rendered row — the
  // single source of truth shared with the AI-emitted row number (STK-1299).
  // Hidden rows leave gaps here; it is NOT a dense counter.
  const [rowNumbers, setRowNumbers] = useState<number[]>([]);
  const [originalColumnWidths, setOriginalColumnWidths] = useState<number[]>(
    [],
  );
  const [originalRowHeights, setOriginalRowHeights] = useState<number[]>([]);

  const columnVisibility = useExcelColumnVisibility(
    columnWidths.length,
    sheetData,
  );
  const { reset: resetColumnVisibility } = columnVisibility;

  // Seed the editable state from the freshly parsed sheet. Fires only when THIS
  // view's parse output changes (new file / new sheet tab), so a view toggle —
  // which owns a separate instance — never triggers it, and the user's resizing
  // and hidden columns survive the flip.
  //
  // useLayoutEffect (not useEffect) so the seed lands BEFORE paint: `workbook`
  // and `parsed` recompute synchronously when `buffer` changes (e.g. switching
  // the active linked pre-order rebuilds the slice), but `sheetData` is derived
  // here. A post-paint effect would let the grid render one frame of the PREVIOUS
  // order's rows while the workbook — and the toolbar download — already point at
  // the new buffer. Running pre-paint keeps the rendered grid, the workbook, and
  // the download in lockstep with no visible stale frame (STK-1269).
  useLayoutEffect(() => {
    if (!parsed) {
      setSheetData([]);
      setColumnWidths([]);
      setRowHeights([]);
      setRowNumbers([]);
      setOriginalColumnWidths([]);
      setOriginalRowHeights([]);
      return;
    }
    setSheetData(parsed.rows);
    setColumnWidths(parsed.columnWidths);
    setRowHeights(parsed.rowHeights);
    setRowNumbers(parsed.rowNumbers);
    setOriginalColumnWidths(parsed.columnWidths);
    setOriginalRowHeights(parsed.rowHeights);
    resetColumnVisibility();
  }, [parsed, resetColumnVisibility]);

  const resetSizes = useCallback(() => {
    setColumnWidths(originalColumnWidths);
    setRowHeights(originalRowHeights);
  }, [originalColumnWidths, originalRowHeights]);

  return {
    workbook,
    hasWorkbook: workbook !== null,
    sheetNames,
    activeSheet,
    setActiveSheet,
    sheetData,
    columnWidths,
    setColumnWidths,
    rowHeights,
    setRowHeights,
    rowNumbers,
    resetSizes,
    ...columnVisibility,
  };
}
